// Google Analytics Enhanced Conversions utility

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | object,
      config?: object
    ) => void;
    dataLayer?: any[];
  }
}

interface UserData {
  email?: string;
  phone_number?: string;
  address?: {
    first_name?: string;
    last_name?: string;
    street?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
}

/**
 * Normalizes user data for Enhanced Conversions
 * - Converts to lowercase
 * - Trims whitespace
 * - Formats phone numbers to E.164 format if possible
 */
const normalizeUserData = (data: UserData): UserData => {
  const normalized: UserData = {};

  if (data.email) {
    normalized.email = data.email.toLowerCase().trim();
  }

  if (data.phone_number) {
    // Remove all non-digit characters
    const digits = data.phone_number.replace(/\D/g, '');
    // If it's a US number without country code, add +1
    if (digits.length === 10) {
      normalized.phone_number = `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      normalized.phone_number = `+${digits}`;
    } else {
      normalized.phone_number = `+${digits}`;
    }
  }

  if (data.address) {
    normalized.address = {};
    
    if (data.address.first_name) {
      normalized.address.first_name = data.address.first_name.toLowerCase().trim();
    }
    if (data.address.last_name) {
      normalized.address.last_name = data.address.last_name.toLowerCase().trim();
    }
    if (data.address.street) {
      normalized.address.street = data.address.street.toLowerCase().trim();
    }
    if (data.address.city) {
      normalized.address.city = data.address.city.toLowerCase().trim();
    }
    if (data.address.region) {
      normalized.address.region = data.address.region.toLowerCase().trim();
    }
    if (data.address.postal_code) {
      normalized.address.postal_code = data.address.postal_code.toLowerCase().trim();
    }
    if (data.address.country) {
      normalized.address.country = data.address.country.toUpperCase().trim();
    }
  }

  return normalized;
};

/**
 * Tracks enhanced conversion with user data
 * Call this function on conversion/thank-you pages
 */
export const trackEnhancedConversion = (userData: UserData) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('gtag not available for enhanced conversion tracking');
    return;
  }

  try {
    const normalizedData = normalizeUserData(userData);
    
    // Remove empty objects/values
    const cleanedData: UserData = {};
    
    if (normalizedData.email) {
      cleanedData.email = normalizedData.email;
    }
    
    if (normalizedData.phone_number) {
      cleanedData.phone_number = normalizedData.phone_number;
    }
    
    if (normalizedData.address && Object.keys(normalizedData.address).length > 0) {
      cleanedData.address = normalizedData.address;
    }

    // Set user data for enhanced conversions
    window.gtag('set', 'user_data', cleanedData);
    
    console.log('Enhanced conversion tracked with user data');
  } catch (error) {
    console.error('Error tracking enhanced conversion:', error);
  }
};

/**
 * Tracks a conversion event
 */
export const trackConversion = (eventName: string, userData?: UserData) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('gtag not available');
    return;
  }

  try {
    // If user data is provided, set it for enhanced conversions
    if (userData) {
      trackEnhancedConversion(userData);
    }

    // Track the conversion event
    window.gtag('event', eventName, {
      send_to: 'G-0NFF9GGNL9'
    });

    console.log(`Conversion event tracked: ${eventName}`);
  } catch (error) {
    console.error('Error tracking conversion:', error);
  }
};
